import { Info, Lock, EyeOff } from 'lucide-react'
import { concatMap, first, iif } from 'rxjs'
import cx from 'classnames'
import { FAVOURITES } from 'constants/requests'
import { refresh } from 'services/ApiService'
import { addUserFavourite, removeUserFavourite } from 'services/FavouritesService'
import TealToolTip from 'components/TealToolTip'
import FavouriteButton from 'components/shared/FavouriteButton'
import TileButton from './TileButton'
import TileInfoPopover from './TileInfoPopover'
import KeywordMatchList, { Match } from './KeywordMatch/KeywordMatchList'
import { comingSoonText } from 'helpers/coming-soon'

interface TileProps {
  id: string
  title: string
  active: boolean
  getUrl: (value: any) => string
  empty: boolean
  dull: boolean
  comingSoon?: boolean
  showInactive: boolean
  category: string
  hideInfo: boolean
  flavour: string
  nodes: any
  contentOverride: boolean
  keyword?: string
  aol: string
  matchingDescendants?: Match[]
  favourites?: string[]
  searchType: string
  restricted?: boolean
}

const Tile = (props: TileProps): JSX.Element | null => {
  const {
    id: sectionId,
    title,
    active,
    getUrl,
    empty,
    dull = false,
    comingSoon = false,
    showInactive = false,
    category,
    hideInfo,
    flavour,
    nodes,
    contentOverride,
    matchingDescendants = [],
    keyword = '',
    aol,
    favourites,
    searchType,
    restricted = false
  } = props

  const isFavourite = favourites?.includes(sectionId) ?? false

  const handleFavouriteClick = (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()

    iif(
      () => isFavourite,
      removeUserFavourite(sectionId, searchType),
      addUserFavourite(sectionId, searchType)
    ).pipe(
      concatMap(() => refresh(FAVOURITES)),
      first()
    ).subscribe()
  }

  if (!(showInactive || active)) return null

  const hasMatchingDescendants = matchingDescendants.length > 0
  const isUnavailable = dull && !empty
  const useAolColour = aol !== '' && flavour !== '' && !isUnavailable
  const aolClass = `aol-${aol}`

  // If this section has exactly one active child, link directly to it
  const section = nodes?.[sectionId]
  const childSectionIds: string[] = section?.childSections ?? []
  const activeChildren = childSectionIds
    .map((childId: string) => nodes[childId])
    .filter((child: any) => child !== null && child !== undefined)
    .filter((child: any) => child.active)

  const tileUrl = activeChildren.length === 1
    ? getUrl({ sectionId, expanded: activeChildren[0].id, focusedId: activeChildren[0].id })
    : getUrl({ sectionId })

  return (
    <div
      className={cx(
        'bg-white tile-border',
        {
          h14: hasMatchingDescendants,
          h8: !hasMatchingDescendants,
          'o-05 not-allowed': empty,
          'tile-unavailable': isUnavailable,
          [aolClass]: useAolColour
        }
      )}
      data-event-category={category}
      data-event-action='Select'
      data-event-label={title}
    >
      <TileButton
        className={cx({ dull: empty || dull }, 'h-100')}
        disableRipple
        fullWidth
        disabled={empty}
        href={tileUrl}
        data-event-category={category}
        data-event-action='Select'
        data-event-label={title}
        data-event-aol={aol}
      >
        <div className='absolute flex items-start flex-column top5 w-100 ph5'>
          <div
            className={cx('lh-title b no-underline f5', { black: !isUnavailable, 'gains-boro': isUnavailable })}
          >
            {title}
          </div>
          <KeywordMatchList
            aol={aol}
            matches={matchingDescendants}
            sectionId={sectionId}
            keyword={keyword}
          />
        </div>
        <div className='absolute ph4 bottom4 flex w-100 items-center justify-between'>
          <div className='flex items-end'>
            {!hideInfo && !empty && (
              <TileInfoPopover {...{ title, getUrl, nodes, sectionId, contentOverride }} />
            )}
            {!isUnavailable && restricted && (
              <TealToolTip
                title='Your current subscription tier offers partial access to this content.'
                bgColor='dark'
                placement='top'
                arrow={false}
              >
                <EyeOff onClick={e => e.preventDefault()} strokeWidth={1.5} size={28} className='grey hover-bg-ghost-white br2 pa2 default-cursor w8 hs8 suez-canal' />
              </TealToolTip>
            )}
            {isUnavailable
              ? (
                <TealToolTip
                  title='Unavailable content'
                  bgColor='dark'
                  placement='top'
                  arrow={false}
                >
                  <Lock strokeWidth={1.5} size={28} className='grey hover-bg-ghost-white br2 pa2' />
                </TealToolTip>
                )
              : (
                <FavouriteButton
                  isFavourite={isFavourite}
                  onClick={handleFavouriteClick}
                />
                )}
          </div>
          {comingSoon && (() => {
            const label = comingSoonText(aol)
            return label !== null && label !== '' ? <span className='bg-cadet-blue white br-pill ph3 pv1 f7 nowrap' style={{ pointerEvents: 'none' }}>{label}</span> : null
          })()}
        </div>
      </TileButton>
      {empty && <EmptyInfoToolTip flavour={flavour} category={category} />}
    </div>
  )
}

const EmptyInfoToolTip: React.FC<{ flavour: string, category: string }> = ({ flavour, category }) => {
  const title = (flavour === 'archive' && category === 'Q&A') ? 'No archived content' : 'No content'
  return (
    <TealToolTip title={title} placement='right' bgColor='dark'>
      <span className='absolute bottom4 left5'>
        <Info strokeWidth={1} className='vm dark-cerulean' />
      </span>
    </TealToolTip>
  )
}

export default Tile
